import { Button, Divider, Flex, Select, Text } from '@bb/elements-web';
import { Popover, Radio, RadioButton, RadioGroup } from 'components';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { featureEnabled } from 'utils';
import BookingTimeSelectContext from './BookingTimeSelectContext';
import AppointmentBookingInternalContext from '../../AppointmentBookingInternalContext';

const DurationPopover: React.FC = ({ children }) => {
  const {
    allowExceedMaxDuration,
    partialAppointmentConfig,
    isListingAgent,
    isAdminWithAccess,
  } = useContext(AppointmentBookingInternalContext);
  const {
    duration,
    setDuration,
    isDurationPopoverOpen: isOpen,
    setIsDurationPopoverOpen: setIsOpen,
  } = useContext(BookingTimeSelectContext);

  const [isCustomDuration, setIsCustomDuration] = useState(false);
  const [customDurationHour, setCustomDurationHour] = useState(1);
  const [customDurationMinute, setCustomDurationMinute] = useState(0);

  const actualMaxDuration = useMemo(() => {
    // 7 * 60 is larger than the maximum option
    if (isListingAgent || isAdminWithAccess || allowExceedMaxDuration)
      return 7 * 60;
    return partialAppointmentConfig!.maxDuration;
  }, [
    isListingAgent,
    isAdminWithAccess,
    allowExceedMaxDuration,
    partialAppointmentConfig,
  ]);

  const maxDurationText = useMemo(() => {
    const { maxDuration } = partialAppointmentConfig!;
    const maxHour = Math.floor(maxDuration / 60);
    const maxMinute = maxDuration % 60;
    if (maxHour && maxMinute) return `${maxHour} hr, ${maxMinute} min`;
    if (maxHour) return `${maxHour} hr`;
    if (maxMinute) return `${maxMinute} min`;
  }, [partialAppointmentConfig]);

  useEffect(() => {
    if (!isCustomDuration) return;
    setDuration(customDurationHour * 60 + customDurationMinute);
  }, [isCustomDuration, customDurationHour, customDurationMinute, setDuration]);

  const handleSetCustomDurationHour = (hour: number) => {
    if (hour * 60 + customDurationMinute > actualMaxDuration) {
      setCustomDurationMinute(0);
    }
    setCustomDurationHour(hour);
  };

  const durationInputs = isCustomDuration ? (
    <Flex vertical gap="small">
      <Select<number, { label: string; value: number }>
        dataCy="duration-select-hour"
        label="Hour"
        size="small"
        value={customDurationHour}
        onChange={v => handleSetCustomDurationHour(v)}
        options={[0, 1, 2, 3, 4, 5, 6]
          .filter(hour => hour * 60 <= actualMaxDuration)
          .map(v => ({
            label: String(v),
            value: v,
          }))}
      />
      <Select<number, { label: string; value: number }>
        dataCy="duration-select-minute"
        label="Minute"
        size="small"
        value={customDurationMinute}
        onChange={v => setCustomDurationMinute(v)}
        options={[0, 15, 30, 45]
          .filter(
            minute => minute + customDurationHour * 60 <= actualMaxDuration,
          )
          .map(v => ({
            label: String(v),
            value: v,
          }))}
      />
    </Flex>
  ) : (
    <RadioGroup
      size="small"
      value={duration}
      onChange={e => setDuration(e.target.value)}
    >
      <Flex vertical gap="small">
        <Radio value={15}>15 Minutes</Radio>
        {actualMaxDuration >= 30 && <Radio value={30}>30 Minutes</Radio>}
        {actualMaxDuration >= 45 && <Radio value={45}>45 Minutes</Radio>}
        {actualMaxDuration >= 60 && <Radio value={60}>60 Minutes</Radio>}
      </Flex>
    </RadioGroup>
  );

  return (
    <Popover
      showArrow={false}
      content={
        <Flex vertical gap="small">
          <div>
            <Text thickness="bold">Select Duration</Text>
            <Text> (max {maxDurationText})</Text>
            <Divider marginSize="small" />
          </div>
          {actualMaxDuration > 60 && (
            <RadioGroup
              size="middle"
              fullWidth
              dataCy="timeSelector-durationType"
              buttonJustify="center"
              label="Duration Type"
              buttonStyle="solid"
              value={isCustomDuration}
              onChange={e => setIsCustomDuration(e.target.value)}
            >
              <RadioButton value={false}>Standard</RadioButton>
              <RadioButton value>Custom</RadioButton>
            </RadioGroup>
          )}

          {durationInputs}

          <Divider marginSize="none" />
          {featureEnabled('supraRebrandingFeature', 1) ? (
            <Button
              dataCy="timeSelectorDone-btn"
              type="primary"
              size="small"
              onClick={() => setIsOpen(false)}
            >
              Done
            </Button>
          ) : (
            <Button
              dataCy="timeSelectorDone-btn"
              type="primary"
              size="small"
              onClick={() => setIsOpen(false)}
            >
              Done
            </Button>
          )}
        </Flex>
      }
      open={isOpen}
      placement="left"
      trigger={[]}
    >
      {children}
    </Popover>
  );
};

export default DurationPopover;
